import React from 'react';
import { Check, Circle, LockKeyhole } from 'lucide-react';
import type { ReportDependencyStep } from '../../../lib/pathToGo/reportPlan';

export interface TestFitDependencyChainProps {
  steps: readonly ReportDependencyStep[];
}

/**
 * A compact engineering single-line diagram for the immutable Test Fit
 * authority chain. Completed sections are energized, the first open node is
 * amber, and downstream nodes stay visibly de-energized.
 *
 * Rendered by the new-analysis wizard. Moved from `pathToGo/` when the Path to
 * GO tab code was deleted (Path to GO removal, stage 3); unchanged.
 */
export const TestFitDependencyChain: React.FC<TestFitDependencyChainProps> = ({
  steps,
}) => (
  <ol
    className="mt-3 flex flex-col gap-0 sm:flex-row sm:items-start"
    aria-label="Generation and Impact to Test Fit dependency chain"
    data-testid="test-fit-dependency-chain"
  >
    {steps.map((step, index) => {
      const complete = step.status === 'complete';
      const current = step.status === 'current';
      const blocked = step.status === 'blocked';
      const energized = complete || current;
      return (
        <li
          key={step.id}
          className="relative flex min-w-0 flex-1 items-start gap-2 pb-3 last:pb-0 sm:block sm:pb-0 sm:text-center"
          data-testid={`test-fit-chain-${step.id}`}
          data-status={step.status}
          aria-current={current || blocked ? 'step' : undefined}
        >
          {index > 0 && (
            <span
              aria-hidden="true"
              className={`absolute left-[9px] -top-3 h-3 w-px sm:left-auto sm:right-1/2 sm:top-[9px] sm:h-px sm:w-1/2 ${
                energized ? 'bg-primary' : 'bg-border'
              }`}
            />
          )}
          {index < steps.length - 1 && (
            <span
              aria-hidden="true"
              className={`absolute left-[9px] top-5 h-[calc(100%-1.25rem)] w-px sm:left-1/2 sm:top-[9px] sm:h-px sm:w-1/2 ${
                complete ? 'bg-primary' : 'bg-border'
              }`}
            />
          )}
          <span
            className={`relative z-10 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border bg-card transition-colors motion-reduce:transition-none sm:mx-auto ${
              complete
                ? 'border-primary bg-primary text-primary-foreground'
                : current
                  ? 'border-warning text-warning shadow-[0_0_8px_var(--warning)]'
                  : blocked
                    ? 'border-danger text-danger'
                    : 'border-border text-text-muted'
            }`}
          >
            {complete
              ? <Check className="h-3 w-3" aria-hidden="true" />
              : blocked
                ? <LockKeyhole className="h-3 w-3" aria-hidden="true" />
                : <Circle className="h-2 w-2 fill-current" aria-hidden="true" />}
          </span>
          <span
            className={`type-meta leading-tight sm:mt-1.5 sm:block ${
              current
                ? 'font-bold text-warning'
                : blocked
                  ? 'font-bold text-danger'
                  : complete
                    ? 'text-text-primary'
                    : 'text-text-muted'
            }`}
          >
            {step.label}
          </span>
        </li>
      );
    })}
  </ol>
);

export default TestFitDependencyChain;
